import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";

import { t } from "@lingui/core/macro";
import { useMutation, useQuery } from "@tanstack/react-query";
import { compact } from "lodash/fp";
import { DateTime } from "luxon";

import { toast } from "@readai/common/hooks/useToasts";
import { isResponseErrorWithStatus } from "@readai/common/utils";

import {
  cancelRssLinkCalendarEvent,
  getRssLinkCalendarEvent,
} from "@app/booking/api/rssLinkCalendarEvents";
import ROUTES from "@app/booking/routes";

export const getQueryKey = (eventId?: string) =>
  compact(["rssLinkCalendarEvent", eventId]);

const useRssLinkCalendarEvent = (existingEventId?: string | null) => {
  const { eventId: eventIdFromParams } = useParams();
  const navigate = useNavigate();
  const eventId = existingEventId ?? eventIdFromParams;

  const getEvent = useQuery({
    queryKey: getQueryKey(eventId),
    queryFn: async () => {
      if (!eventId) return null;

      try {
        const event = await getRssLinkCalendarEvent(eventId);

        if (!event) {
          navigate(ROUTES.EVENT_NOT_FOUND);
        }

        const eventIsInPast = DateTime.now() > event.startTime;

        if (eventIsInPast) {
          navigate(ROUTES.eventEndedById(eventId), { replace: true });
        }

        return event;
      } catch (error) {
        if (isResponseErrorWithStatus(error, 404)) {
          navigate(ROUTES.EVENT_NOT_FOUND);
        } else {
          navigate(ROUTES.EVENT_ERROR);
        }
        return null;
      }
    },
    enabled: Boolean(eventId),
  });

  const cancelEvent = useMutation({
    mutationFn: cancelRssLinkCalendarEvent,
    onError: () => {
      toast.error(
        t`Something went wrong cancelling this event. Please refresh the page or try again later.`,
      );
    },
  });

  return { getEvent, cancelEvent };
};

export default useRssLinkCalendarEvent;
